import { createSlice } from '@reduxjs/toolkit';
import { Service } from 'services/Services';
import { RequestApi } from 'RequestApi';
import { NotificationMessage } from 'components/notification-message/NotificationMessage';
import { setProgressingDocument, setViewingDocument, updateDocument } from 'components/dynamic/dynamic-page/DynamicPageSlice';

const initialState = {
  formLoading: false,
  record: null,
  pageInfo: null,
  showRemarkActionApproval: false,
  hostClusterName: '',
  workflowRequestType: null,
  loadingInsertRecordWorkflow: false,
  cardFormLoading: false,
};

const scholarshipRequestFormDocumentFormSlice = createSlice({
  name: 'scholarshipRequestFormDocumentForm',
  initialState,
  reducers: {
    setPageInfo(state, action) {
      state.pageInfo = action.payload;
    },
    setCardFormLoading(state, action) {
      state.cardFormLoading = action.payload;
    },
    setFormLoading(state, action) {
      state.formLoading = action.payload;
    },
    setRecord(state, action) {
      state.record = action.payload;
    },
    setShowRemarkActionApproval(state, action) {
      state.showRemarkActionApproval = action.payload;
    },
    setHostClusterName(state, action) {
      state.hostClusterName = action.payload;
    },
    setWorkflowRequestType(state, action) {
      state.workflowRequestType = action.payload;
    },
    setLoadingInsertRecordWorkflow(state, action) {
      state.loadingInsertRecordWorkflow = action.payload;
    },
    setProgressing(state, action) {
      state.progressing = action.payload;
    },

    // ---- Line ----- //
    setStoreLineRecord(state, action) {
      const { subRecord, keyTableLine } = action.payload;
      const tmpRecord = { ...state.record };
      tmpRecord[keyTableLine].push(subRecord);
      state.record = tmpRecord;
      if (keyTableLine === 'employee_scholarship_request_line') {
        state.cardFormLoading = true;
      }
    },

    setUpdateLineRecord(state, action) {
      const { record, subRecord, keyTableLine, lineUnid } = action.payload;
      const resRecord = record;
      const fieldNames = Object.keys(resRecord);
      const tmpRecord = { ...state.record };
      fieldNames.forEach((fieldName) => {
        tmpRecord[fieldName] = resRecord[fieldName];
      });
      const index = tmpRecord[keyTableLine].findIndex((element) => element.id.toString() === lineUnid.toString());
      if (index !== -1) {
        tmpRecord[keyTableLine][index] = subRecord;
        state.record = tmpRecord;
      }
    },
  },
});

const {
  setPageInfo,
  setFormLoading,
  setHostClusterName,
  setProgressing,
  setWorkflowRequestType,
  setLoadingInsertRecordWorkflow,
  setCardFormLoading,
  setStoreLineRecord,
} = scholarshipRequestFormDocumentFormSlice.actions;

export const { setRecord, setUpdateLineRecord, setShowRemarkActionApproval } = scholarshipRequestFormDocumentFormSlice.actions;

export const initPageInfo =
  ({ informationPage, document }) =>
    async (dispatch) => {
      try {
        dispatch(setFormLoading(true));
        dispatch(setPageInfo(informationPage));

        let url = `${informationPage.url}/index-card?`;
        url += `auth_token=${Service.userToken}`;

        const data = JSON.stringify({
          data_unid: document.id,
        });

        RequestApi(url, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              dispatch(setRecord(res.data.record));
              dispatch(setHostClusterName(res.data.host_cluster_name));
              dispatch(setWorkflowRequestType(res.data.workflow_request_types));

              if (!document.status) {
                dispatch(setViewingDocument(res.data.record));
              }
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }

            dispatch(setFormLoading(false));
          })
          .catch((err) => {
            console.log(err);
            dispatch(setFormLoading(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setFormLoading(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const updateRecord =
  ({ url, unid, fieldName, valueChanged, record }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));

        let urlRequest = `${url}/update-record?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';

        const data = JSON.stringify({
          field_name: fieldName,
          value: valueChanged,
          unid,
        });

        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const resRecord = res.data.record;
              const fieldNames = Object.keys(resRecord);
              const tmpRecord = { ...record };

              fieldNames.forEach((objFieldName) => {
                tmpRecord[objFieldName] = resRecord[objFieldName];
              });
              dispatch(setRecord(tmpRecord));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const actionApproval =
  ({ pageInfo, unid, dataApproval }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));

        let urlRequest = `/approval-workflow-activity/action-approval?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';

        const data = JSON.stringify({
          unid,
          object_id: pageInfo.object_id,
          status: dataApproval.status,
          record_unid: dataApproval.activity.id,
          source: 'card',
        });

        RequestApi(urlRequest, { data })
          .then((res) => {
            if (res.data.status === 'success') {
              dispatch(setShowRemarkActionApproval(true));
              dispatch(setProgressingDocument(false));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const approvalWorkflowActivity =
  ({ pageInfo, unid, dataApproval, remark }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));

        const status = dataApproval.status.toString().toLowerCase();

        let token = '';
        let wfToken = '';

        if (status === 'accept') {
          const urlParams = new URLSearchParams(dataApproval.activity.accept_url);
          token = urlParams.get('token');
          wfToken = urlParams.get('wf_token');
        } else if (status === 'dispose') {
          const urlParams = new URLSearchParams(dataApproval.activity.dispose_url);
          token = urlParams.get('token');
          wfToken = urlParams.get('wf_token');
        } else if (status === 'return') {
          const urlParams = new URLSearchParams(dataApproval.activity.return_url);
          token = urlParams.get('token');
          wfToken = urlParams.get('wf_token');
        } else if (status === 'reject') {
          const urlParams = new URLSearchParams(dataApproval.activity.reject_url);
          token = urlParams.get('token');
          wfToken = urlParams.get('wf_token');
        }

        let urlRequest = `/approval-workflow-activity/${status}?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';

        const data = JSON.stringify({
          unid,
          object_id: pageInfo.object_id,
          status: dataApproval.status,
          record_unid: dataApproval.activity.id,
          source: 'card',
          mail_content: remark,
          token,
          wf_token: wfToken,
        });

        RequestApi(urlRequest, { data })
          .then((res) => {
            if (res.data.status === 'success') {
              dispatch(setViewingDocument(res.data.record));
              dispatch(updateDocument(res.data.record));
              dispatch(setRecord(res.data.record));
              dispatch(setProgressingDocument(false));
              dispatch(setShowRemarkActionApproval(false));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

// ----- Line ----- //

export const updateLineRecord =
  ({ url, unid, objectGroupField, valueChanged, lineUnid, keyTableLine, tableName }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));

        let urlRequest = `${url}/update-line-record?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=api';

        const data = JSON.stringify({
          field_name: objectGroupField.field_name,
          unid,
          value: valueChanged,
          sub_unid: lineUnid,
          table_name: tableName,
        });

        RequestApi(urlRequest, {
          data,
        }).then((res) => {
          if (res.status === 200) {
            if (res.data.status === 'success') {
              dispatch(setUpdateLineRecord({ record: res.data.record, subRecord: res.data.sub_record, lineUnid, keyTableLine }));
            } else {
              dispatch(setUpdateLineRecord({ record: res.data.record, subRecord: res.data.sub_record, lineUnid, keyTableLine }));
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
          } else {
            NotificationMessage({ message: res.message, status: 'warning' });
          }
          dispatch(setProgressingDocument(false));
        });
      } catch (ex) {
        NotificationMessage({ message: ex.toString(), status: 'error' });
        dispatch(setProgressingDocument(false));
      }
    };

export const addNewLineRecord =
  ({ url, unid, tableName, keyTableLine }) =>
    async (dispatch) => {
      try {
        dispatch(setCardFormLoading(true));

        let urlRequest = `${url}/store-line-record?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=api';

        const data = JSON.stringify({
          unid,
          table_name: tableName,
        });

        RequestApi(urlRequest, {
          data,
        }).then((res) => {
          if (res.data.status === 'success') {
            dispatch(setStoreLineRecord({ subRecord: res.data.sub_record, keyTableLine }));
          } else {
            NotificationMessage({ message: res.data.msg, status: res.data.status });
          }
          dispatch(setCardFormLoading(false));
        });
      } catch (ex) {
        dispatch(setCardFormLoading(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      } finally {
        dispatch(setCardFormLoading(false));
      }
    };

export const deleteLineRecord =
  ({ url, unid, subUnid, record, informationPage, ObjectGroupID, keyTableLine }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));

        let urlRequest = `${url}/delete-line-record?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';
        const data = JSON.stringify({
          sub_unid: subUnid,
          unid,
          object_id: informationPage.object_id,
          object_group_id: ObjectGroupID,
        });

        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const fieldNames = keyTableLine;

              dispatch(
                setRecord({
                  ...record,
                  [fieldNames]: record[fieldNames].filter((ele) => ele.id !== res.data.sub_record.id),
                })
              );
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

// WorkFlow Committee Funtion
export const deleteAllRecordWorkflow =
  ({ url, unid, record }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));
        dispatch(setProgressing(true));

        let urlRequest = `${url}/delete-all-record-workflow?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';

        const data = JSON.stringify({
          unid,
        });

        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const resRecord = res.data.record;
              const fieldNames = Object.keys(resRecord);
              const tmpRecord = { ...record };

              fieldNames.forEach((objFieldName) => {
                tmpRecord[objFieldName] = resRecord[objFieldName];
              });

              dispatch(setRecord(tmpRecord));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        dispatch(setProgressing(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const updateWorkFlowCommittee =
  ({ url, unid, username, stepType, record }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));
        dispatch(setProgressing(true));

        let urlRequest = `${url}/update-record-workflow?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';

        const data = JSON.stringify({
          username,
          step_type: stepType,
          unid,
          sub_unid: 0,
        });

        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const resRecord = res.data.record;
              const fieldNames = Object.keys(resRecord);
              const tmpRecord = { ...record };

              fieldNames.forEach((objFieldName) => {
                tmpRecord[objFieldName] = resRecord[objFieldName];
              });

              dispatch(setRecord(tmpRecord));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        dispatch(setProgressing(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const deleteWorkFlowCommittee =
  ({ url, unid, subUnid, record }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));
        dispatch(setProgressing(true));

        let urlRequest = `${url}/delete-record-workflow?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';
        const data = JSON.stringify({
          sub_unid: subUnid,
          unid,
        });

        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const resRecord = res.data.record;
              const fieldNames = Object.keys(resRecord);
              const tmpRecord = { ...record };
              fieldNames.forEach((objFieldName) => {
                tmpRecord[objFieldName] = resRecord[objFieldName];
              });
              dispatch(setRecord(tmpRecord));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        dispatch(setProgressing(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const insertRecordWorkflow =
  ({ url, unid, record }) =>
    async (dispatch) => {
      try {
        dispatch(setLoadingInsertRecordWorkflow(true));

        let urlRequest = `${url}/insert-record-workflow?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';
        const data = JSON.stringify({
          unid,
          step_type: 'Check By',
        });
        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const resRecord = res.data.record;
              const fieldNames = Object.keys(resRecord);
              const tmpRecord = { ...record };
              fieldNames.forEach((objFieldName) => {
                tmpRecord[objFieldName] = resRecord[objFieldName];
              });
              dispatch(setRecord(tmpRecord));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setLoadingInsertRecordWorkflow(false));
          })
          .catch((err) => {
            dispatch(setLoadingInsertRecordWorkflow(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setLoadingInsertRecordWorkflow(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };

export const updateLineWorkflowCommittee =
  ({ url, subUnid, unid, record, stepType, username }) =>
    async (dispatch) => {
      try {
        dispatch(setProgressingDocument(true));
        dispatch(setProgressing(true));

        let urlRequest = `${url}/change-record-workflow?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';
        const data = JSON.stringify({
          unid,
          sub_unid: subUnid,
          step_type: stepType,
          username,
        });

        RequestApi(urlRequest, {
          data,
        })
          .then((res) => {
            if (res.data.status === 'success') {
              const resRecord = res.data.record;
              const fieldNames = Object.keys(resRecord);
              const tmpRecord = { ...record };
              fieldNames.forEach((objFieldName) => {
                tmpRecord[objFieldName] = resRecord[objFieldName];
              });
              dispatch(setRecord(tmpRecord));
            } else {
              NotificationMessage({ message: res.data.msg, status: res.data.status });
            }
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
          })
          .catch((err) => {
            dispatch(setProgressingDocument(false));
            dispatch(setProgressing(false));
            NotificationMessage({ message: err.toString(), status: 'error' });
          });
      } catch (ex) {
        dispatch(setProgressingDocument(false));
        dispatch(setProgressing(false));
        NotificationMessage({ message: ex.toString(), status: 'error' });
      }
    };
// End WorkFlow Committee Funtion

const scholarshipRequestFormDocumentFormReducer = scholarshipRequestFormDocumentFormSlice.reducer;

export default scholarshipRequestFormDocumentFormReducer;
